import type { ReactNode } from 'react';

import type { GetItemPropsOptions } from 'downshift';

import type {
  AutocompleteGetItemProps,
  DefaultItem,
  DropdownGetItemProps,
  GenericItem,
  GetItemKey,
} from '../types';

type ExtraOptions = {
  index?: number;
  isHighlighted: boolean;
  text?: ReactNode;
  subtext?: ReactNode;
};

/**
 * Wrapper for getItemProps which adds the "isHighlighted" property on the given item.
 * When index resolution fails (indexGetter returns undefined or negative), index is set to -1
 * so getItemProps still receives a number. isHighlighted is only true when index is valid
 * (>= 0) and equals highlightedIndex, so -1 never conflates with Downshift's use of -1 for
 * "no selection".
 *
 * @param getItemProps prop getter returned from downshift hook
 * @param highlightedIndex prop returned from downshift hook
 * @param selectedItem
 * @param indexGetter
 * @returns modified version of getItemProps
 */
export const wrapGetItemProps =
  <TItem extends GenericItem>({
    getItemProps,
    highlightedIndex,
    selectedItem,
    indexGetter,
    getItemKey,
  }: {
    getItemProps:
      | DropdownGetItemProps<TItem, ExtraOptions>
      | AutocompleteGetItemProps<TItem, ExtraOptions>;
    highlightedIndex: number;
    selectedItem: TItem | null;
    indexGetter:
      | null
      | ((item: TItem, indexHint?: number) => number | undefined);
    getItemKey?: GetItemKey<TItem>;
  }) =>
  ({
    item,
    index: indexProp,
    isSelected: isSelectedProp,
    ...rest
  }: GetItemPropsOptions<TItem>) => {
    const resolvedIndex = indexGetter?.(item, indexProp) ?? indexProp;
    const index =
      resolvedIndex !== undefined && resolvedIndex >= 0 ? resolvedIndex : -1;

    let isSelected: boolean;
    if (isSelectedProp !== undefined) {
      isSelected = isSelectedProp;
    } else if (getItemKey && selectedItem !== null) {
      isSelected = getItemKey(item) === getItemKey(selectedItem);
    } else {
      isSelected = selectedItem === item;
    }

    const toReturn = getItemProps({
      item,
      index,
      isHighlighted: index >= 0 && index === highlightedIndex,
      isSelected,
      text: (item as unknown as DefaultItem).label,
      subtext: (item as unknown as DefaultItem).subtext,
      ...rest,
    });

    return {
      ...toReturn,
      // Override downshift's aria-selected (which reflects highlighted index in useCombobox)
      // to consistently reflect selection state across both autocomplete and regular dropdown.
      'aria-selected': isSelected,
      disabled: toReturn['aria-disabled'],
    };
  };
